import { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { language, setLanguage, t } = useLanguage();
  const { logout, guest } = useAuth();
  const menuButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const navLinks = [
    { name: t('home'), href: '#home' },
    { name: t('venue'), href: '#venue' },
    { name: t('schedule'), href: '#schedule' },
    { name: t('rsvp'), href: '#rsvp', isRsvp: true },
    { name: t('faq'), href: '#faq' },
    { name: t('accommodations'), href: '#accommodations' },
    { name: t('explore'), href: '#explore' },
    { name: t('viewRegistry'), href: '#registry' },
    { name: t('gallery'), href: '#gallery' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ['gallery', 'registry', 'explore', 'accommodations', 'faq', 'rsvp', 'schedule', 'venue', 'home'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const menu = mobileMenuRef.current;
    if (!menu) return;

    const focusableSelectors = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(menu.querySelectorAll(focusableSelectors));
    if (focusableElements.length > 0) focusableElements[0].focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { closeMobileMenu(); return; }
      if (e.key !== 'Tab') return;
      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    menu.addEventListener('keydown', handleKeyDown);
    return () => menu.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    menuButtonRef.current?.focus();
  };

  const scrollTo = (e, href) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const offset = 70;
      const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    closeMobileMenu();
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-sm py-3' : 'bg-white py-4'
        }`}
        data-testid="navigation"
      >
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-[auto_1fr_auto] items-center">
          {/* Left side: Logo + Language Switcher */}
          <div className="flex items-center gap-6">
            {/* Logo */}
            <a
              href="#home"
              onClick={(e) => scrollTo(e, '#home')}
              className="font-serif text-xl font-normal text-[#2D2A26]"
              data-testid="nav-logo"
            >
              A&T
            </a>

            {/* Language Switcher */}
            <div className="flex items-center gap-1.5 text-sm">
              <button
                onClick={() => setLanguage('fr')}
                aria-label="Passer en français"
                aria-pressed={language === 'fr'}
                className={`px-3 py-1 rounded-full transition-all ${
                  language === 'fr'
                    ? 'bg-[#B5985A] text-white font-medium'
                    : 'text-[#999] hover:text-[#2D2A26] hover:bg-[#F5F0E8]'
                }`}
                data-testid="lang-fr"
              >
                FR
              </button>
              <button
                onClick={() => setLanguage('en')}
                aria-label="Switch to English"
                aria-pressed={language === 'en'}
                className={`px-3 py-1 rounded-full transition-all ${
                  language === 'en'
                    ? 'bg-[#B5985A] text-white font-medium'
                    : 'text-[#999] hover:text-[#2D2A26] hover:bg-[#F5F0E8]'
                }`}
                data-testid="lang-en"
              >
                EN
              </button>
            </div>
          </div>

          {/* Desktop Navigation (centered between left and right) */}
          <div className="hidden min-[1000px]:flex items-center justify-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                {...(link.external
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : { onClick: (e) => scrollTo(e, link.href) }
                )}
                className={link.isRsvp
                  ? `text-sm px-5 py-2 rounded transition-colors ${activeSection === 'rsvp' ? 'bg-[#9A7F48] text-white' : 'bg-[#B5985A] text-white hover:bg-[#9A7F48]'}`
                  : `text-sm transition-colors ${!link.external && activeSection === link.href.replace('#', '') ? 'text-[#B5985A]' : 'text-[#2D2A26] hover:text-[#B5985A]'}`
                }
                data-testid={link.isRsvp ? 'nav-rsvp-button' : `nav-link-${link.external ? 'registry' : link.href.replace('#', '')}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Empty placeholder for the center column on mobile */}
          <div className="min-[1000px]:hidden" />

          {/* Right side: Logout (desktop) or Logout + Menu (mobile) */}
          <div className="flex items-center gap-2 justify-self-end">
            <button
              onClick={logout}
              className="hidden min-[1000px]:block text-sm text-[#999] hover:text-[#B5985A] transition-colors"
              data-testid="logout-button"
            >
              {t('logout')}
            </button>
            <button
              onClick={logout}
              className="min-[1000px]:hidden text-sm text-[#999] hover:text-[#B5985A] transition-colors px-2 py-2"
              data-testid="logout-button-mobile"
            >
              {t('logout')}
            </button>
            <button
              ref={menuButtonRef}
              className="min-[1000px]:hidden p-2 text-[#2D2A26]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label={t('toggleMenu')}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('mobileMenuLabel')}
            id="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-white flex flex-col items-center justify-center"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center gap-6">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <a
                    href={link.href}
                    {...(link.external
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : { onClick: (e) => scrollTo(e, link.href) }
                    )}
                    className={link.isRsvp
                      ? 'bg-[#B5985A] text-white text-lg px-8 py-3 rounded hover:bg-[#9A7F48] transition-colors inline-block'
                      : `font-serif text-2xl transition-colors ${!link.external && activeSection === link.href.replace('#', '') ? 'text-[#B5985A]' : 'text-[#2D2A26] hover:text-[#B5985A]'}`
                    }
                  >
                    {link.name}
                  </a>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
